export const API_CACHE_STORAGE_KEY = "gaamed.provider.api-cache.v2";
export const MUTATION_QUEUE_STORAGE_KEY = "gaamed.provider.mutation-queue.v2";
export const RESOURCE_MAP_STORAGE_KEY = "gaamed.provider.resource-map.v2";
const LEGACY_STORAGE_KEYS = [
  "gaamed.provider.api-cache.v1",
  "gaamed.provider.mutation-queue.v1",
  "gaamed.provider.resource-map.v1",
];
const QUARANTINE_STORAGE_KEY = "gaamed.provider.offline-quarantine.v1";
const LEASE_STORAGE_KEY = "gaamed.provider.sync-lease.v1";
const DEFAULT_SCOPE = "anonymous";
let activeScope = DEFAULT_SCOPE;
let activeRevision = "initial";
const OFFLINE_STORE_EVENT = "gaamed:provider-offline-store-changed";
const SESSION_REVISION_STORAGE_KEY = "gaamed.provider.session-revision.v1";
const sessionChannel = typeof BroadcastChannel !== "undefined"
  ? new BroadcastChannel("gaamed-provider-session")
  : null;

export interface CachedApiEntry<T> {
  updated_at: string;
  value: T;
}

export interface QueuedProviderMutation {
  body: unknown;
  client_mutation_id?: string;
  id: string;
  label: string;
  local_resource_id?: string;
  method: "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  queued_at: string;
  response_id_field?: string;
  dead_letter?: boolean;
  dead_letter_reason?: string;
  identity_scope?: string;
}

type CachedApiMap = Record<string, CachedApiEntry<unknown>>;

function canUseStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function readJson<T>(storageKey: string, fallback: T) {
  if (!canUseStorage()) {
    return fallback;
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) {
      return fallback;
    }

    return JSON.parse(rawValue) as T;
  } catch {
    return fallback;
  }
}

function writeJson(storageKey: string, value: unknown) {
  if (!canUseStorage()) {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // Ignore storage quota and serialization failures. Network paths still work.
  }
}

function scopedKey(key: string) {
  return `${key}:${activeScope}`;
}

export function quarantineLegacyOfflineState() {
  if (!canUseStorage()) return;
  const quarantined = readJson<Record<string, unknown>>(QUARANTINE_STORAGE_KEY, {});
  let changed = false;
  for (const key of LEGACY_STORAGE_KEYS) {
    const value = window.localStorage.getItem(key);
    if (value !== null) {
      quarantined[key] = value;
      window.localStorage.removeItem(key);
      changed = true;
    }
  }
  if (changed) writeJson(QUARANTINE_STORAGE_KEY, quarantined);
}

export function setOfflineIdentity(identity: string | null) {
  quarantineLegacyOfflineState();
  const nextScope = identity ? encodeURIComponent(identity) : DEFAULT_SCOPE;
  // A fresh tab joining the same authenticated account adopts the existing
  // revision instead of aborting requests in every already-open tab.
  if (nextScope !== activeScope && nextScope !== getOfflineSessionRevision().identity_scope) {
    const revision = typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `revision-${Date.now()}-${Math.random()}`;
    const next = { identity_scope: nextScope, revision };
    writeJson(SESSION_REVISION_STORAGE_KEY, next);
    sessionChannel?.postMessage(next);
  }
  activeScope = nextScope;
  activeRevision = getOfflineSessionRevision().revision;
  emitOfflineStoreChange();
}

export function getOfflineIdentityScope() {
  return activeScope;
}

export function getOfflineSessionRevision() {
  return readJson<{ identity_scope: string; revision: string }>(SESSION_REVISION_STORAGE_KEY, {
    identity_scope: DEFAULT_SCOPE,
    revision: "initial",
  });
}

export function isOfflineSessionCurrent() {
  const shared = getOfflineSessionRevision();
  return shared.identity_scope === activeScope && shared.revision === activeRevision;
}

function notifyOfflineStoreChange() {
  emitOfflineStoreChange();
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === SESSION_REVISION_STORAGE_KEY) notifyOfflineStoreChange();
  });
}
sessionChannel?.addEventListener("message", notifyOfflineStoreChange);

export function purgeOfflineState() {
  if (!canUseStorage()) return;
  const deadLetters = listAllQueuedProviderMutations().filter((mutation) => mutation.dead_letter);
  if (deadLetters.length > 0) {
    const quarantine = readJson<Record<string, unknown>>(QUARANTINE_STORAGE_KEY, {});
    quarantine[`dead-letters:${activeScope}:${Date.now()}`] = deadLetters;
    writeJson(QUARANTINE_STORAGE_KEY, quarantine);
  }
  for (const key of [API_CACHE_STORAGE_KEY, MUTATION_QUEUE_STORAGE_KEY, RESOURCE_MAP_STORAGE_KEY]) {
    window.localStorage.removeItem(scopedKey(key));
  }
  emitOfflineStoreChange();
}

export interface SyncLease {
  owner: string;
  expires_at: number;
  fencing: number;
}

export function acquireSyncLease(owner: string, ttlMs = 15_000): SyncLease | null {
  if (!canUseStorage()) return null;
  const now = Date.now();
  const current = readJson<SyncLease | null>(scopedKey(LEASE_STORAGE_KEY), null);
  if (current && current.expires_at > now && current.owner !== owner) return null;
  const lease = { owner, expires_at: now + ttlMs, fencing: (current?.fencing ?? 0) + 1 };
  writeJson(scopedKey(LEASE_STORAGE_KEY), lease);
  const reread = readJson<SyncLease | null>(scopedKey(LEASE_STORAGE_KEY), null);
  return reread?.owner === owner && reread.fencing === lease.fencing ? reread : null;
}

export function isSyncLeaseCurrent(lease: SyncLease) {
  const current = readJson<SyncLease | null>(scopedKey(LEASE_STORAGE_KEY), null);
  return Boolean(
    current && current.owner === lease.owner && current.fencing === lease.fencing && current.expires_at > Date.now(),
  );
}

export async function withOfflineStoreLock<T>(callback: () => Promise<T> | T) {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (locks) {
    return locks.request("gaamed-provider-offline-store", { mode: "exclusive" }, callback);
  }
  return callback();
}

export async function withOfflineReplayLock<T>(callback: () => Promise<T>, background: boolean) {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (locks) return locks.request("gaamed-provider-offline-replay", { mode: "exclusive" }, callback);
  // Without an atomic browser lock, all replay is disabled rather than
  // pretending localStorage read/write sequences are mutually exclusive.
  void callback;
  void background;
  return undefined;
}

export function renewSyncLease(lease: SyncLease, ttlMs = 15_000) {
  const current = readJson<SyncLease | null>(scopedKey(LEASE_STORAGE_KEY), null);
  if (!current || current.owner !== lease.owner || current.fencing !== lease.fencing) return null;
  const renewed = { ...current, expires_at: Date.now() + ttlMs };
  writeJson(scopedKey(LEASE_STORAGE_KEY), renewed);
  return readJson<SyncLease | null>(scopedKey(LEASE_STORAGE_KEY), null);
}

export function releaseSyncLease(lease: SyncLease) {
  const current = readJson<SyncLease | null>(scopedKey(LEASE_STORAGE_KEY), null);
  if (current?.owner === lease.owner && current.fencing === lease.fencing) {
    window.localStorage.removeItem(scopedKey(LEASE_STORAGE_KEY));
  }
}

function emitOfflineStoreChange() {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new Event(OFFLINE_STORE_EVENT));
}

export function subscribeToOfflineStore(listener: () => void) {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  window.addEventListener(OFFLINE_STORE_EVENT, listener);
  return () => window.removeEventListener(OFFLINE_STORE_EVENT, listener);
}

export function createOfflineRecordId(prefix: string) {
  const randomSuffix = Math.random().toString(36).slice(2, 8);
  return `offline-${prefix}-${Date.now().toString(36)}-${randomSuffix}`;
}

export function readCachedApiResponse<T>(path: string) {
  const cache = readJson<CachedApiMap>(scopedKey(API_CACHE_STORAGE_KEY), {});
  const entry = cache[path];
  return entry ? (entry.value as T) : null;
}

export function listCachedApiEntries() {
  return readJson<CachedApiMap>(scopedKey(API_CACHE_STORAGE_KEY), {});
}

export function writeCachedApiResponse<T>(path: string, value: T) {
  writeCachedApiResponseForScope(path, value, activeScope);
}

export function writeCachedApiResponseForScope<T>(path: string, value: T, scope: string) {
  const cache = readJson<CachedApiMap>(`${API_CACHE_STORAGE_KEY}:${scope}`, {});
  cache[path] = {
    updated_at: new Date().toISOString(),
    value,
  };
  writeJson(`${API_CACHE_STORAGE_KEY}:${scope}`, cache);
  emitOfflineStoreChange();
}

export function removeCachedApiResponse(path: string) {
  const cache = readJson<CachedApiMap>(scopedKey(API_CACHE_STORAGE_KEY), {});
  if (!(path in cache)) {
    return;
  }

  delete cache[path];
  writeJson(scopedKey(API_CACHE_STORAGE_KEY), cache);
  emitOfflineStoreChange();
}

export function updateCachedApiResponses(
  updater: (path: string, value: unknown) => unknown,
) {
  const cache = readJson<CachedApiMap>(scopedKey(API_CACHE_STORAGE_KEY), {});
  let didChange = false;
  const updatedAt = new Date().toISOString();

  for (const [path, entry] of Object.entries(cache)) {
    const nextValue = updater(path, entry.value);
    if (nextValue === entry.value) {
      continue;
    }

    cache[path] = {
      updated_at: updatedAt,
      value: nextValue,
    };
    didChange = true;
  }

  if (!didChange) {
    return;
  }

  writeJson(scopedKey(API_CACHE_STORAGE_KEY), cache);
  emitOfflineStoreChange();
}

export function listOfflineResourceMappings() {
  return readJson<Record<string, string>>(scopedKey(RESOURCE_MAP_STORAGE_KEY), {});
}

export function getOfflineResourceMapping(localId: string) {
  return listOfflineResourceMappings()[localId] ?? null;
}

export function writeOfflineResourceMapping(localId: string, serverId: string) {
  const mappings = listOfflineResourceMappings();
  mappings[localId] = serverId;
  writeJson(scopedKey(RESOURCE_MAP_STORAGE_KEY), mappings);
  emitOfflineStoreChange();
}

function listAllQueuedProviderMutations() {
  return readJson<QueuedProviderMutation[]>(scopedKey(MUTATION_QUEUE_STORAGE_KEY), []);
}

export function listQueuedProviderMutations() {
  return listAllQueuedProviderMutations().filter(
    (mutation) => !mutation.dead_letter,
  );
}

export function listDeadLetterProviderMutations() {
  return listAllQueuedProviderMutations().filter((mutation) => mutation.dead_letter);
}

export function getQueuedProviderMutationCount() {
  return listQueuedProviderMutations().length;
}

export function enqueueProviderMutation(
  mutation: Omit<QueuedProviderMutation, "id" | "queued_at">,
) {
  const queuedMutation: QueuedProviderMutation = {
    ...mutation,
    id: createOfflineRecordId("mutation"),
    queued_at: new Date().toISOString(),
    identity_scope: activeScope,
  };
  queuedMutation.client_mutation_id ??= queuedMutation.id;
  const nextQueue = [...listAllQueuedProviderMutations(), queuedMutation];
  writeJson(scopedKey(MUTATION_QUEUE_STORAGE_KEY), nextQueue);
  emitOfflineStoreChange();
  return queuedMutation;
}

export function removeQueuedProviderMutation(mutationId: string) {
  const nextQueue = listAllQueuedProviderMutations().filter((mutation) => mutation.id !== mutationId);
  writeJson(scopedKey(MUTATION_QUEUE_STORAGE_KEY), nextQueue);
  emitOfflineStoreChange();
}

export function deadLetterProviderMutation(mutationId: string, reason: string) {
  const queue = listAllQueuedProviderMutations();
  const nextQueue = queue.map((mutation) =>
    mutation.id === mutationId
      ? { ...mutation, dead_letter: true, dead_letter_reason: reason }
      : mutation,
  );
  writeJson(scopedKey(MUTATION_QUEUE_STORAGE_KEY), nextQueue);
  emitOfflineStoreChange();
}

export function retryDeadLetterProviderMutation(mutationId: string) {
  const nextQueue = listAllQueuedProviderMutations().map((mutation) =>
    mutation.id === mutationId
      ? { ...mutation, dead_letter: false, dead_letter_reason: undefined }
      : mutation,
  );
  writeJson(scopedKey(MUTATION_QUEUE_STORAGE_KEY), nextQueue);
  emitOfflineStoreChange();
}

// Legacy unscoped values are never eligible for the first authenticated user.
quarantineLegacyOfflineState();
